import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigationType, useLocation } from "react-router-dom";
import { api } from "../../api";
import { saveListState, readListState } from "../../utils/listStateCache";
import { ApiTwoTone } from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import {
  FilterToggle,
  FilterPanel,
  ActivePills,
  EmptyState,
  CalIc,
  MoonIc,
  prettyDate,
} from "../filters/FilterControls";
import { formatDateKeyLocal, formatDateLabel } from "./videoDateUtils";
import "./videolectureslisting.css";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Moon } from "lucide-react";
import SaveToPlaylistButton from "../../pages/playlists/SaveToPlaylistButton";
import PlaylistTip from "../../pages/playlists/PlaylistTip";
import { videoKey } from "../../pages/playlists/playlistsModel";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const VL_URL = `${API_BASE}/video-lectures`;
const PAGE_SIZE = 20;

// Filter chips → backend `filter` value ("" = no filter / All).
const VIDEO_FILTERS = [
  { label: "All", value: "" },
  { label: "Lectures", value: "video-lecture" },
  { label: "Milaad", value: "milaad" },
  { label: "Sama", value: "samaa" },
];

// Panel fields only — search and the type chips live outside the panel and are
// not counted by the Filters badge. Video has no khanqah, so it is three
// dropdowns where the audio archive has four.
const BLANK_FILTERS = {
  year: "",
  month: "",
  islamicMonth: "",
  from: "",
  to: "",
};

const EMPTY_FACETS = { years: [], months: [], islamicMonths: [] };

const PANEL_FIELDS = (facets) => [
  {
    key: "year",
    label: "Year",
    icon: <CalIc />,
    placeholder: "Any year",
    options: facets.years,
  },
  {
    key: "month",
    label: "Month",
    icon: <CalIc />,
    placeholder: "Any month",
    options: facets.months,
  },
  {
    key: "islamicMonth",
    label: "Islamic Month",
    icon: <MoonIc />,
    placeholder: "Any month",
    options: facets.islamicMonths,
  },
];

const VIDEO_TYPE_LABEL = {
  "video-lecture": "Lectures",
  milaad: "Milaad",
  samaa: "Sama",
  "annual-event": "Event",
};

async function fetchVideoFacets() {
  const { data } = await api.get("/video-lectures/facets");
  if (!data?.ok) throw new Error(data?.message || "Failed to fetch filters");
  const f = data.data || {};
  const asOption = (v) => ({ value: String(v), label: String(v) });
  return {
    years: (f.years || []).map(asOption),
    months: (f.months || []).map((m) => ({
      value: String(m.value),
      label: m.label,
    })),
    islamicMonths: (f.islamicMonths || []).map(asOption),
  };
}

function ytUrl(videoId) {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

function ytThumbFromId(videoId) {
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "";
}
async function updateLectureOnServer({
  livestreamId,
  titleEn,
  titleUr,
  livestreamType,
}) {
  const { data } = await api.put("/video-lectures/updateVideoLecture", {
    livestreamId,
    titleEn,
    titleUr,
    livestreamType,
  });

  const isOk = data?.ok === true || data?.success === true;
  if (!isOk) throw new Error(data?.message || "Failed to update lecture");

  return data;
}

async function updateActiveStatusOnServer({ livestreamId, isActive }) {
  const { data } = await api.patch(
    `/video-lectures/updateLiveStreamStatus/${livestreamId}`,
    { isActive },
  );

  const isOk = data?.ok === true || data?.success === true;

  if (!isOk) {
    throw new Error(data?.message || "Failed to update stream status");
  }

  return data;
}
function formatWeekdayLocal(isoOrDate) {
  return new Date(isoOrDate).toLocaleDateString("en-US", { weekday: "long" });
}

// livestreamType → display badge (mirrors the audio type badges)
const VIDEO_TYPE_BADGE = {
  "video-lecture": { label: "Lecture", cls: "lecture" },
  milaad: { label: "Milaad", cls: "milaad" },
  samaa: { label: "Sama", cls: "sama" },
  "annual-event": { label: "Event", cls: "event" },
};

function VideoTypeBadge({ type }) {
  const t = VIDEO_TYPE_BADGE[type];
  if (!t) return null;
  return <span className={`yh-type-badge yh-type-badge--${t.cls}`}>{t.label}</span>;
}

function Row({ item, onEditLecture, onDelete }) {
  const { user } = useAuth();

  const isAdmin =
    user?.role === "admin" ||
    user?.role === "superadmin" ||
    user?.roles?.includes?.("admin") ||
    user?.isAdmin === true;

  const thumb = ytThumbFromId(item.videoId);

  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // inline edit state (per-row)
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitleEn, setDraftTitleEn] = useState(item.titleEn);
  const [draftTitleUr, setDraftTitleUr] = useState(item.titleUr);
  const [draftType, setDraftType] = useState(item.livestreamType);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // keep drafts in sync if item updates from parent
  useEffect(() => {
    if (!isEditing) {
      setDraftTitleEn(item.titleEn);
      setDraftTitleUr(item.titleUr);
      setDraftType(item.livestreamType);
    }
  }, [item.titleEn, item.titleUr, item.livestreamType, isEditing]);

  useEffect(() => {
    if (!open) return;

    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  function cancelEdit() {
    setIsEditing(false);
    setDraftTitleEn(item.titleEn);
    setDraftTitleUr(item.titleUr);
    setDraftType(item.livestreamType);
    setErr("");
  }

  async function saveEdit() {
    const nextEn = draftTitleEn.trim();
    const nextUr = draftTitleUr.trim();
    if (!nextEn) return;

    setSaving(true);
    setErr("");

    try {
      await onEditLecture?.(item, {
        titleEn: nextEn,
        titleUr: nextUr,
        livestreamType: draftType,
      });
      setIsEditing(false);
    } catch (e) {
      setErr(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`yh-row ${isAdmin ? "yh-row--admin" : "yh-row--user"}`}>
      <Link
        className="yh-thumbLink"
        to={`/video-lectures/watch/${item.livestreamId}`}
      >
        <div className="yh-thumb">
          <img src={thumb} alt="" />
        </div>
      </Link>

      <div className="yh-meta">
        {/* Title area */}
        {!isEditing ? (
          <>
            <div className="yh-titleRow">
              <Link
                className="yh-titleLink"
                to={`/video-lectures/watch/${item.livestreamId}`}
              >
                {item.titleUr ? (
                  <div className="yh-titles">
                    <div className="yh-title-ur" lang="ur" dir="rtl">
                      {item.titleUr}
                    </div>
                    <div className="yh-title-en">
                      {item.titleEn || item.title}
                    </div>
                  </div>
                ) : (
                  <div className="yh-title">{item.titleEn || item.title}</div>
                )}
              </Link>
            </div>
            <div className="yh-metaRow">
              <VideoTypeBadge type={item.livestreamType} />
              {item.islamicMonth && (
                <>
                  {VIDEO_TYPE_BADGE[item.livestreamType] && (
                    <span className="yh-meta-dot" />
                  )}
                  <span className="yh-month">
                    <Moon size={11} strokeWidth={2} />
                    <span className="yh-month-text" dir="rtl">
                      {item.islamicMonth}
                    </span>
                  </span>
                </>
              )}
              {(VIDEO_TYPE_BADGE[item.livestreamType] || item.islamicMonth) && (
                <span className="yh-meta-dot" />
              )}
              <span className="yh-meta-date">{item.startedAtText}</span>
              <SaveToPlaylistButton
                itemKey={videoKey(item.livestreamId)}
                title={item.titleEn || item.title}
                kind="video"
                className="yh-save"
              />
            </div>
          </>
        ) : (
          <div className="yh-titleEditRow">
            <input
              className="yh-titleInput"
              value={draftTitleEn}
              onChange={(e) => setDraftTitleEn(e.target.value)}
              disabled={saving}
              autoFocus
              placeholder="Title (English)"
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                if (e.key === "Escape") cancelEdit();
              }}
            />

            <input
              className="yh-titleInput yh-titleInput--ur"
              value={draftTitleUr}
              onChange={(e) => setDraftTitleUr(e.target.value)}
              disabled={saving}
              dir="rtl"
              lang="ur"
              placeholder="عنوان (اردو) — اختیاری"
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                if (e.key === "Escape") cancelEdit();
              }}
            />

            <div className="yh-titleEditActions">
              <select
                className="yh-typeSelect"
                value={draftType}
                onChange={(e) => setDraftType(e.target.value)}
                disabled={saving}
              >
                <option value="video-lecture">Video Lecture</option>
                <option value="samaa">Samaa</option>
                <option value="milaad">Milaad</option>
                <option value="other">Other</option>
              </select>

              <button
                className="yh-iconBtn yh-iconBtnOk"
                onClick={saveEdit}
                disabled={saving || !draftTitleEn.trim()}
                aria-label="Save"
                title="Save"
                type="button"
              >
                <CheckIcon fontSize="small" />
              </button>

              <button
                className="yh-iconBtn yh-iconBtnCancel"
                onClick={cancelEdit}
                disabled={saving}
                aria-label="Cancel edit"
                title="Cancel"
                type="button"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="yh-sub">
            <span className="yh-views">{item.startedAtText}</span>
          </div>
        )}

        {err ? <div className="yh-editError">{err}</div> : null}
      </div>

      {isAdmin && (
        <div className="yh-menuWrap" ref={menuRef}>
          <button
            className="yh-menu"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            ⋮
          </button>

          {open && (
            <div className="yh-dd">
              <button
                className="yh-ddItem"
                onClick={() => {
                  setOpen(false);
                  setIsEditing(true);
                  setErr("");
                }}
              >
                <EditOutlined fontSize="small" />
                Edit
              </button>

              <button
                className="yh-ddItem yh-ddDanger"
                onClick={() => onDelete?.(item)}
              >
                <DeleteOutline fontSize="small" />
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

async function fetchJson(url) {
  // cache-buster only (no custom headers => avoids CORS preflight/network error)
  const sep = url.includes("?") ? "&" : "?";
  const urlNoCache = `${url}${sep}_=${Date.now()}`;

  const { data } = await api.get(`/video-lectures${urlNoCache}`);

  const isOk = data?.ok === true || data?.success === true;
  if (!isOk) throw new Error(data?.message || "API request failed");

  return data;
}

/**
 * Feed controller (race-proof, strictmode-proof)
 * - No UI/CSS changes
 * - Stable merge-by-id + sort
 * - Ignores stale responses
 * - Blocks loadMore until first page is committed
 */
const initialFeed = {
  streams: [],
  nextCursor: null,
  hasMore: true,
  phase: "idle", // idle | loadingFirst | ready | loadingMore | error
  error: "",
  lastAppliedSeq: 0,
  serverQuery: "",
  total: 0,
  typeCounts: null,
};

function sortDescByStartedAt(a, b) {
  return new Date(b.startedAt) - new Date(a.startedAt);
}

function mergeUniqueSorted(prevList, newItems) {
  const map = new Map(prevList.map((x) => [x.id, x]));
  for (const x of newItems) map.set(x.id, x);
  return Array.from(map.values()).sort(sortDescByStartedAt);
}

function feedReducer(state, action) {
  switch (action.type) {
    case "LOAD_FIRST_START":
      return { ...state, phase: "loadingFirst", error: "" };

    case "LOAD_FIRST_SUCCESS": {
      const { seq, items, nextCursor, hasMore, serverQuery, total, typeCounts } =
        action.payload;
      if (seq < state.lastAppliedSeq) return state;
      return {
        ...state,
        streams: mergeUniqueSorted([], items),
        nextCursor,
        hasMore,
        phase: "ready",
        error: "",
        lastAppliedSeq: seq,
        serverQuery: serverQuery ?? state.serverQuery, // ✅
        total: total ?? items.length,
        typeCounts: typeCounts ?? state.typeCounts,
      };
    }

    case "SEARCH_START": {
      const { serverQuery } = action.payload;
      return {
        ...state,
        phase: "loadingFirst",
        error: "",
        serverQuery, // ✅ update which query backend is using
        nextCursor: null, // ✅ reset pagination for new query
        hasMore: true,
        // ✅ keep streams as-is (so UI doesn't flicker/blank)
      };
    }
    case "UPDATE_LECTURE_LOCAL": {
      const { id, ...fields } = action.payload;
      return {
        ...state,
        streams: state.streams.map((s) =>
          s.id === id ? { ...s, ...fields, title: fields.titleEn } : s,
        ),
      };
    }

    case "LOAD_MORE_START":
      return { ...state, phase: "loadingMore", error: "" };

    case "LOAD_MORE_SUCCESS": {
      const { seq, items, nextCursor, hasMore } = action.payload;
      if (seq < state.lastAppliedSeq) return state; // ignore stale response
      return {
        ...state,
        streams: mergeUniqueSorted(state.streams, items),
        nextCursor,
        hasMore,
        phase: "ready",
        error: "",
        lastAppliedSeq: seq,
      };
    }

    case "FAIL": {
      const { seq, error } = action.payload;
      if (seq < state.lastAppliedSeq) return state;
      return { ...state, phase: "error", error, lastAppliedSeq: seq };
    }

    default:
      return state;
  }
}

export default function VideoLecturesListing({ title, filter }) {
  // adjust to your navbar height
  const NAVBAR_HEIGHT = 100;

  // ─── Restore after a "back" ───────────────────────────────────────────────
  // Only on POP (browser/router back or forward). Arriving fresh — a nav click,
  // a reload — must start clean, so PUSH/REPLACE ignore the cache.
  const navigationType = useNavigationType();
  const { pathname } = useLocation();
  const restoredRef = useRef(undefined);
  if (restoredRef.current === undefined) {
    restoredRef.current =
      navigationType === "POP" ? readListState(pathname) || null : null;
  }
  const restored = restoredRef.current;

  const [queryInput, setQueryInput] = useState(restored?.queryInput ?? "");
  const [queryDebounced, setQueryDebounced] = useState(
    restored?.queryDebounced ?? "",
  );

  // active filter chip ("" = All); seeded from the optional `filter` prop
  const [activeFilter, setActiveFilter] = useState(
    restored?.activeFilter ?? filter ?? "",
  );

  // collapsible filter panel (shared with the Audio archive)
  const [filters, setFilters] = useState(restored?.filters ?? BLANK_FILTERS);
  const [panelOpen, setPanelOpen] = useState(false);
  const [facets, setFacets] = useState(EMPTY_FACETS);

  // infinite scroll sentinel
  const sentinelRef = useRef(null);

  // strictmode + race controls
  const reqSeqRef = useRef(0);
  const didLoadFirstRef = useRef(false);

  // Feed state (single source of truth)
  const [feed, dispatch] = React.useReducer(
    feedReducer,
    restored?.feed ?? initialFeed,
  );

  // Every page already loaded is back in state, so the first fetch would only
  // overwrite it with page one. Skip it exactly once.
  const skipFirstFetchRef = useRef(Boolean(restored));

  // This page scrolls its own container (.yh-page is height:100vh /
  // overflow-y:scroll), not the window — so the scroll offset comes from the
  // element, not window.scrollY.
  const scrollerRef = useRef(null);
  const scrollTopRef = useRef(restored?.scrollTop ?? 0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      scrollTopRef.current = el.scrollTop;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Latest snapshot, kept fresh for the unmount handler below.
  const snapshotRef = useRef(null);
  snapshotRef.current = {
    feed,
    queryInput,
    queryDebounced,
    activeFilter,
    filters,
  };

  useEffect(
    () => () => {
      saveListState(pathname, {
        ...snapshotRef.current,
        scrollTop: scrollTopRef.current,
      });
    },
    [pathname],
  );

  // Put the scroll back after the restored rows have painted. Two frames:
  // the first commits them, the second runs once the layout is settled.
  useEffect(() => {
    if (!restored) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        if (scrollerRef.current) {
          scrollerRef.current.scrollTop = restored.scrollTop || 0;
        }
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [restored]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    item: null,
  });
  // Normalize DB items -> UI items (same as your UI)
  function normalize(items = []) {
    return items.map((x) => {
      const url = ytUrl(x.videoId);

      const id = x._id
        ? String(x._id)
        : `${x.videoId}-${new Date(x.startedAt).getTime()}`;

      return {
        id,
        livestreamId: x.livestreamId,
        videoId: x.videoId,
        livestreamType: x.livestreamType || "video-lecture",
        youtubeUrl: url,
        titleEn: x.titleEn || x.title || "(No title)",
        titleUr: x.titleUr || "",
        title: x.titleEn || x.title || "(No title)", // legacy alias
        islamicMonth: x.islamicMonth || "",
        channel: "",
        viewsText: "",
        durationText: "",
        startedAt: x.startedAt,
        startedAtText: new Date(x.startedAt).toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        dateKey: formatDateKeyLocal(x.startedAt),
      };
    });
  }

  useEffect(() => {
    const html = document.documentElement; // ✅ <html>
    const body = document.body;

    html.classList.add("yh-lockScrollbar");
    body.classList.add("yh-lockScrollbar");

    return () => {
      html.classList.remove("yh-lockScrollbar");
      body.classList.remove("yh-lockScrollbar");
    };
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryDebounced(queryInput.trim());
    }, 450); // 350–600ms is typical; 450 feels good

    return () => clearTimeout(t);
  }, [queryInput]);
  // Panel filters as a query-string fragment. A string (rather than the object)
  // keeps it stable across renders, so it can drive the fetch effect directly.
  const panelParams = useMemo(
    () =>
      Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`)
        .join(""),
    [filters],
  );

  // ─── Filter state helpers ────────────────────────────────────────────────
  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  // The badge counts panel fields only — search and type read off the chips.
  const panelCount = Object.values(filters).filter(Boolean).length;

  const monthLabel = (value) =>
    facets.months.find((m) => m.value === value)?.label || value;

  const pills = useMemo(() => {
    const out = [];
    if (queryDebounced) out.push({ key: "q", label: `“${queryDebounced}”` });
    if (activeFilter) {
      out.push({
        key: "type",
        label: VIDEO_TYPE_LABEL[activeFilter] || activeFilter,
      });
    }
    if (filters.year) out.push({ key: "year", label: filters.year });
    if (filters.month) {
      out.push({ key: "month", label: monthLabel(filters.month) });
    }
    if (filters.islamicMonth) {
      out.push({ key: "islamicMonth", label: filters.islamicMonth });
    }
    if (filters.from) {
      out.push({ key: "from", k: "From", label: prettyDate(filters.from) });
    }
    if (filters.to) {
      out.push({ key: "to", k: "To", label: prettyDate(filters.to) });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDebounced, activeFilter, filters, facets.months]);

  const removePill = (key) => {
    if (key === "q") setQueryInput("");
    else if (key === "type") setActiveFilter("");
    else setFilters((prev) => ({ ...prev, [key]: "" }));
  };

  const resetAll = () => {
    setQueryInput("");
    setActiveFilter("");
    setFilters(BLANK_FILTERS);
  };

  // Filter dropdown options (derived from the catalogue)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchVideoFacets();
        if (!cancelled) setFacets(next);
      } catch (err) {
        // Non-fatal: the panel still opens, its dropdowns just have no options.
        console.error("Failed to fetch filter options:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // First page (runs once even in React 18 StrictMode dev)
  useEffect(() => {
    // Restored from a back navigation — the feed is already populated.
    if (skipFirstFetchRef.current) {
      skipFirstFetchRef.current = false;
      return;
    }

    const seq = ++reqSeqRef.current;

    dispatch({
      type: "SEARCH_START",
      payload: { serverQuery: queryDebounced },
    });

    (async () => {
      try {
        const qParam = queryDebounced
          ? `&q=${encodeURIComponent(queryDebounced)}`
          : "";

        const filterParam =
          activeFilter && activeFilter.trim() !== ""
            ? `&filter=${encodeURIComponent(activeFilter)}`
            : "";

        const data = await fetchJson(
          `/history?limit=${PAGE_SIZE}${qParam}${filterParam}${panelParams}`,
        );

        const items = normalize(data.items || []);
        const nextCursor = data.nextCursor || null;
        const hasMore =
          Boolean(nextCursor) && (data.items || []).length === PAGE_SIZE;

        dispatch({
          type: "LOAD_FIRST_SUCCESS",
          payload: {
            seq,
            items,
            nextCursor,
            hasMore,
            serverQuery: queryDebounced,
            total: data.total,
            typeCounts: data.typeCounts,
          },
        });
      } catch (e) {
        dispatch({
          type: "FAIL",
          payload: { seq, error: e?.message || "Failed to load streams" },
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDebounced, activeFilter, panelParams]);

  async function confirmDelete() {
    const rowItem = deleteDialog.item;
    if (!rowItem) return;

    try {
      await updateActiveStatusOnServer({
        livestreamId: rowItem.livestreamId,
        isActive: false,
      });

      // remove from UI
      dispatch({
        type: "LOAD_FIRST_SUCCESS",
        payload: {
          seq: reqSeqRef.current,
          items: feed.streams.filter((s) => s.id !== rowItem.id),
          nextCursor: feed.nextCursor,
          hasMore: feed.hasMore,
        },
      });
    } catch (e) {
      alert(e.message || "Failed to delete lecture");
    } finally {
      setDeleteDialog({ open: false, item: null });
    }
  }

  // Load more (cursor) - cannot run until first page is ready
  async function loadMore() {
    if (feed.phase !== "ready") return; // ✅ blocks before first page & while loading
    if (!feed.hasMore || !feed.nextCursor) return;
    const qParam = feed.serverQuery
      ? `&q=${encodeURIComponent(feed.serverQuery)}`
      : "";
    const seq = ++reqSeqRef.current;
    dispatch({ type: "LOAD_MORE_START" });

    try {
      const filterParam =
        activeFilter && activeFilter.trim() !== ""
          ? `&filter=${encodeURIComponent(activeFilter)}`
          : "";

      const data = await fetchJson(
        `/history?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(
          feed.nextCursor,
        )}${qParam}${filterParam}${panelParams}`,
      );

      const items = normalize(data.items || []);
      const nextCursor = data.nextCursor || null;
      const hasMore =
        Boolean(nextCursor) && (data.items || []).length === PAGE_SIZE;

      dispatch({
        type: "LOAD_MORE_SUCCESS",
        payload: { seq, items, nextCursor, hasMore },
      });
    } catch (e) {
      dispatch({
        type: "FAIL",
        payload: { seq, error: e?.message || "Failed to load more streams" },
      });
    }
  }

  // IntersectionObserver (infinite scroll) - attach only when feed is ready
  useEffect(() => {
    if (feed.phase !== "ready") return;

    const el = sentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0,
      },
    );

    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed.phase, feed.nextCursor, feed.hasMore]);

  const grouped = useMemo(() => {
    const q = queryInput.trim().toLowerCase();

    const filtered = !q
      ? feed.streams
      : feed.streams.filter((s) =>
          `${s.titleEn} ${s.titleUr} ${s.channel}`.toLowerCase().includes(q),
        );

    const map = new Map();
    for (const item of filtered) {
      if (!map.has(item.dateKey)) map.set(item.dateKey, []);
      map.get(item.dateKey).push(item);
    }

    return Array.from(map.entries());
  }, [queryInput, feed.streams]);

  const loading = feed.phase === "loadingFirst";
  const loadingMore = feed.phase === "loadingMore";
  const error = feed.phase === "error" ? feed.error : "";

  const isInitialLoad = loading && feed.streams.length === 0; // first ever load
  const isSearching = loading && feed.streams.length > 0 && !!queryDebounced; // search refresh

  return (
    <div className="yh-page yh-layout" ref={scrollerRef}>
      {/* centered search under menu (like YouTube) */}
      <h1 className="page-title">{title}</h1>
      <div className="yh-searchBarWrap">
        <div className="vl-search-row">
          <div className="yh-searchBar">
            <span className="yh-search-ic">
              <svg viewBox="0 0 20 20">
                <circle cx="9" cy="9" r="5.5" />
                <path d="M13.2 13.2 17 17" />
              </svg>
            </span>
            <input
              className="yh-searchInput"
              type="text"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder="Search Video…"
              autoComplete="off"
            />
            {queryInput && (
              <button
                className="yh-search-x"
                onClick={() => setQueryInput("")}
                aria-label="Clear search"
              >
                <svg viewBox="0 0 20 20">
                  <path d="M6 6l8 8M14 6l-8 8" />
                </svg>
              </button>
            )}
          </div>

          <FilterToggle
            open={panelOpen}
            onToggle={() => setPanelOpen((v) => !v)}
            count={panelCount}
          />
        </div>
      </div>

      <div className="vl-filtersWrap">
        <div className="vl-type-filters">
          {VIDEO_FILTERS.map((f) => (
            <button
              key={f.label}
              type="button"
              className={`vl-type-chip ${
                activeFilter === f.value ? "vl-type-chip--active" : ""
              }`}
              onClick={() => setActiveFilter(f.value)}
            >
              {f.label}
              {feed.typeCounts && (
                <span className="n">
                  {feed.typeCounts[f.value || "All"] ?? 0}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {panelOpen && (
        <FilterPanel
          fields={PANEL_FIELDS(facets)}
          values={filters}
          onChange={setFilter}
          onReset={resetAll}
          onClose={() => setPanelOpen(false)}
          total={feed.total}
          columns={3}
        />
      )}

      <ActivePills pills={pills} onRemove={removePill} onReset={resetAll} />

      <div className="yh-container">
        {/* <h1 className="yh-h1">Past Streams</h1> */}
        {isSearching && (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <CircularProgress size={30} sx={{ color: "#F47F22" }} />
          </div>
        )}
        {isInitialLoad ? (
          <div
            className="flex items-center justify-center"
            style={{ minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)` }}
          >
            <CircularProgress size={45} sx={{ color: "#F47F22" }} />
          </div>
        ) : error ? (
          <div className="yh-empty">Error: {error}</div>
        ) : (
          <div className="yh-groups">
            {grouped.length === 0 ? (
              <EmptyState onReset={resetAll} />
            ) : (
              grouped.map(([dateKey, items]) => (
                <div key={dateKey} className="yh-group">
                  <div className="yh-date">
                    <span className="yh-date-day">{formatDateLabel(dateKey)}</span>
                    {items[0]?.startedAt && (
                      <span className="yh-date-weekday">
                        {formatWeekdayLocal(items[0].startedAt)}
                      </span>
                    )}
                    <span className="yh-date-rule" />
                  </div>

                  <div className="yh-list">
                    {items.map((it) => (
                      <Row
                        key={it.id}
                        item={it}
                        onDelete={(rowItem) => {
                          setDeleteDialog({
                            open: true,
                            item: rowItem,
                          });
                        }}
                        onEditLecture={async (
                          rowItem,
                          { titleEn, titleUr, livestreamType },
                        ) => {
                          // Optimistic local update. startedAt is not editable
                          // here — it comes from YouTube — so the row keeps its
                          // date group.
                          dispatch({
                            type: "UPDATE_LECTURE_LOCAL",
                            payload: {
                              id: rowItem.id,
                              titleEn,
                              titleUr,
                              livestreamType,
                            },
                          });

                          try {
                            const saved = await updateLectureOnServer({
                              livestreamId: rowItem.livestreamId,
                              titleEn,
                              titleUr,
                              livestreamType,
                            });
                            // Take the server's version of the row rather than
                            // trusting the optimistic one.
                            if (saved?.item) {
                              dispatch({
                                type: "UPDATE_LECTURE_LOCAL",
                                payload: {
                                  ...normalize([saved.item])[0],
                                  // keep the row's existing identity — the
                                  // reducer matches on it
                                  id: rowItem.id,
                                },
                              });
                            }
                          } catch (e) {
                            // rollback if server fails
                            dispatch({
                              type: "UPDATE_LECTURE_LOCAL",
                              payload: {
                                id: rowItem.id,
                                titleEn: rowItem.titleEn,
                                titleUr: rowItem.titleUr,
                                livestreamType: rowItem.livestreamType,
                              },
                            });
                            throw e;
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Invisible sentinel: does not change UI */}
            <div ref={sentinelRef} style={{ height: 1 }} />

            {/* Optional loader using your existing style */}
            {loadingMore ? (
              <div className="yh-spinnerWrap" style={{ marginTop: 18 }}>
                <CircularProgress size={30} sx={{ color: "#F47F22" }} />
              </div>
            ) : null}
          </div>
        )}
      </div>
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null })}
      >
        <DialogTitle>Delete</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this lecture? It will no longer
            appear in the list.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, item: null })}
            color="inherit"
          >
            Cancel
          </Button>

          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              backgroundColor: "#F47F22",
              "&:hover": { backgroundColor: "#d96f1e" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <PlaylistTip />
    </div>
  );
}
